import * as React from 'react'
import { Check } from 'lucide-react'
import {
  SOLUTION_PRICING_QUANTITY_UNITS,
  basisNeedsQuantity,
  type QuantityBasis,
} from '@lib/enums/solution-pricing-basis.enum'
import type {
  ProblemSolutionDto,
  SolutionPricingDto,
} from '@lib/dto/problem-solution.dto'
import type { TreeProblemDto } from '@lib/dto/tree-problem.dto'
import { parseMinutes } from '@lib/helpers/minutes'
import { isPartialQuantity, parseQuantity } from '@lib/helpers/quantity'
import { priceSolution } from '@lib/helpers/solution-pricing'
import { formatMoney } from '@lib/helpers/money'
import type { Season } from '@lib/enums/season.enum'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SeasonPicker } from '@/field-map/SeasonPicker'
import { cn } from '@/lib/utils'

/**
 * The seasons a problem and its treatments are chosen against: the one it is
 * now, and the one a new problem starts in (the tree's pruning season, or
 * now). A treatment with no usual season starts in its problem's season, and
 * in this one when the problem has none.
 */
export interface TreatmentSeasons {
  current: Season
  fallback: Season
}

/** Whether a half-typed quantity can never become a real one. */
function quantityIsUnreadable(basis: QuantityBasis, text: string): boolean {
  const trimmed = text.trim()
  if (trimmed === '') return false
  switch (basis) {
    case 'TIME':
      return parseMinutes(trimmed) === null
    case 'LINEAR_FEET':
      return !isPartialQuantity(trimmed)
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}

/** The keypad a quantity wants: minutes are whole, feet are not. */
function quantityInputMode(basis: QuantityBasis): 'numeric' | 'decimal' {
  switch (basis) {
    case 'TIME':
      return 'numeric'
    case 'LINEAR_FEET':
      return 'decimal'
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}

/** What to say when a quantity cannot be read. */
function quantityHint(basis: QuantityBasis): string {
  switch (basis) {
    case 'TIME':
      return 'Whole minutes, e.g. 45.'
    case 'LINEAR_FEET':
      return 'A number, e.g. 12 or 12.5.'
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}

/**
 * The number already saved for one basis.
 *
 * Minutes and feet are different fields. Reading feet for a time basis, or
 * the reverse, would show one charge as the other.
 */
function committedQuantity(
  basis: QuantityBasis,
  chosen: TreeProblemDto['solutions'][number],
): string {
  switch (basis) {
    case 'TIME':
      return chosen.minutes?.toString() ?? ''
    case 'LINEAR_FEET':
      return chosen.linearFeet ?? ''
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}

/**
 * A quantity field for one basis a chosen solution is charged on.
 *
 * Holds its own text rather than deriving it from the committed value. Only
 * well-formed quantities are committed, so a derived field would snap "14."
 * back to "14" under the person typing it — the same reason the labor
 * estimate keeps local text.
 */
function QuantityField({
  basis,
  solutionId,
  committed,
  controlClassName,
  onChange,
}: {
  basis: QuantityBasis
  solutionId: string
  committed: string
  controlClassName?: string | undefined
  onChange: (next: string) => void
}) {
  const id = `solution-${solutionId}-${basis}`
  const [text, setText] = React.useState(committed)
  const unreadable = quantityIsUnreadable(basis, text)

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id} className="w-16 shrink-0 text-xs">
          {SOLUTION_PRICING_QUANTITY_UNITS[basis]}
        </Label>
        <Input
          id={id}
          // `numeric` for minutes, which are whole: a phone should not offer a
          // decimal point for a field that cannot take one.
          inputMode={quantityInputMode(basis)}
          aria-invalid={unreadable}
          className={cn('w-24', controlClassName)}
          value={text}
          onChange={(event) => {
            setText(event.target.value)
            onChange(event.target.value)
          }}
        />
      </div>
      {unreadable ? (
        <p className="text-xs text-danger">{quantityHint(basis)}</p>
      ) : null}
    </div>
  )
}

/**
 * The treatments chosen for one problem on one tree.
 *
 * A tap to choose, then a field per basis the solution is charged on. Trunk
 * diameter is deliberately absent: it is measured once on the tree, so a
 * solution priced by it reads the tree's figure rather than asking again here
 * — which would give one tree three chances to disagree with itself.
 *
 * The amount beside each is computed by the API and arrives with the tree. It
 * is shown rather than hidden because a surveyor standing under the tree is
 * the last person who can catch a treatment that priced at nothing because
 * nobody set a rate, or because the diameter was never taken.
 */
export function ProblemSolutions({
  problem,
  available,
  dbhInches,
  ratePerHour,
  seasons,
  controlClassName,
  onChange,
}: {
  problem: TreeProblemDto
  available: ProblemSolutionDto[]
  /** The tree's diameter, read by treatments charged by it. */
  dbhInches: string | null
  /** The crew rate, for treatments charged by time with no override. */
  ratePerHour: string | null
  seasons: TreatmentSeasons
  controlClassName?: string | undefined
  onChange: (next: TreeProblemDto) => void
}) {
  // Archived solutions stay visible if already chosen, and are never offered.
  const offered = available.filter(
    (solution) =>
      solution.archivedAt === null ||
      problem.solutions.some((chosen) => chosen.solutionId === solution.id),
  )
  if (offered.length === 0) return null

  const chosenById = new Map(
    problem.solutions.map((solution) => [solution.solutionId, solution]),
  )

  /**
   * What one treatment costs, worked out here rather than read off the row.
   *
   * The `amount` the API sends is correct only until somebody changes a
   * quantity, and this screen is built to work with no network at all — so a
   * figure that waited for a round trip would be stale online and permanently
   * wrong offline, which is the one place it is actually read.
   *
   * The same `priceSolution` the API uses, so the number under a surveyor's
   * thumb and the number on the client's proposal come from one implementation.
   */
  function priceOf(
    solution: ProblemSolutionDto,
    chosen: TreeProblemDto['solutions'][number],
  ): string {
    const cents = priceSolution(
      { pricings: solution.pricings, organizationRatePerHour: ratePerHour },
      {
        minutes: chosen.minutes,
        linearFeet: chosen.linearFeet,
        dbhInches,
      },
    )
    return cents === null ? '—' : formatMoney(cents)
  }

  function toggle(solution: ProblemSolutionDto): void {
    const already = chosenById.get(solution.id)
    onChange({
      ...problem,
      solutions:
        already === undefined
          ? [
              ...problem.solutions,
              {
                // A local row until the server answers: the id and the amount
                // are its to assign, and the field app must not invent either.
                id: solution.id,
                solutionId: solution.id,
                label: solution.label,
                minutes: null,
                linearFeet: null,
                amount: null,
                // Its usual season when the directory has one: dormant oil
                // starts in winter, and the surveyor changes it if not.
                // Otherwise when its problem is dealt with.
                season:
                  solution.usualSeason ?? problem.season ?? seasons.fallback,
              },
            ]
          : problem.solutions.filter(
              (chosen) => chosen.solutionId !== solution.id,
            ),
    })
  }

  function setSeason(solutionId: string, season: Season): void {
    onChange({
      ...problem,
      solutions: problem.solutions.map((chosen) =>
        chosen.solutionId === solutionId ? { ...chosen, season } : chosen,
      ),
    })
  }

  /**
   * Commit a typed quantity, or leave the tree alone.
   *
   * Nothing half-typed is written. A tree write is validated as ONE batch, so
   * a value the server rejects — "14." on its way to "14.5", or anything
   * `parseFloat` would quietly accept — fails every other queued edit for the
   * site with it, non-retriably.
   *
   * Minutes go through `parseMinutes` rather than `Number.parseInt`, which
   * returns NaN for the bare "." this decimal keypad offers, renders the
   * string "NaN" back into the field, and is silently turned into null by
   * `JSON.stringify` on the way out — losing the time rather than refusing it.
   * Feet stay feet: a basis this function does not know is not stored as a
   * length.
   */
  function setQuantity(
    solutionId: string,
    basis: QuantityBasis,
    raw: string,
  ): void {
    const trimmed = raw.trim()
    const cleared = trimmed === ''

    switch (basis) {
      case 'TIME': {
        const minutes = parseMinutes(trimmed)
        if (!cleared && minutes === null) return
        onChange({
          ...problem,
          solutions: problem.solutions.map((chosen) =>
            chosen.solutionId === solutionId
              ? { ...chosen, minutes: cleared ? null : minutes }
              : chosen,
          ),
        })
        return
      }
      case 'LINEAR_FEET': {
        const quantity = parseQuantity(trimmed)
        if (!cleared && quantity === null) return
        onChange({
          ...problem,
          solutions: problem.solutions.map((chosen) =>
            chosen.solutionId === solutionId
              ? { ...chosen, linearFeet: cleared ? null : quantity }
              : chosen,
          ),
        })
        return
      }
      default: {
        const unhandled: never = basis
        return unhandled
      }
    }
  }

  return (
    <div className="space-y-1.5">
      <Label>What are we doing about it?</Label>
      <ul className="space-y-1.5">
        {offered.map((solution) => {
          const chosen = chosenById.get(solution.id)
          const needsDbh = solution.pricings.some(
            (pricing) => pricing.basis === 'DBH_INCH',
          )
          return (
            <li key={solution.id}>
              <button
                type="button"
                onClick={() => {
                  toggle(solution)
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm',
                  chosen === undefined
                    ? 'border-default bg-surface text-secondary-text'
                    : 'border-brand bg-brand-subtle text-primary-text',
                  controlClassName,
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    chosen === undefined
                      ? 'border-strong'
                      : 'border-brand bg-brand text-inverse-text',
                  )}
                >
                  {chosen === undefined ? null : <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1 truncate">{solution.label}</span>
                {chosen === undefined ? null : (
                  <span className="shrink-0 text-xs tabular-nums text-secondary-text">
                    {priceOf(solution, chosen)}
                  </span>
                )}
              </button>

              {chosen === undefined ? null : (
                <div className="mt-1.5 space-y-1.5 pl-6">
                  {solution.pricings
                    .filter(
                      (
                        pricing,
                      ): pricing is SolutionPricingDto & {
                        basis: QuantityBasis
                      } => basisNeedsQuantity(pricing.basis),
                    )
                    .map((pricing) => (
                      <QuantityField
                        key={pricing.basis}
                        basis={pricing.basis}
                        solutionId={solution.id}
                        controlClassName={controlClassName}
                        committed={committedQuantity(pricing.basis, chosen)}
                        onChange={(next) => {
                          setQuantity(solution.id, pricing.basis, next)
                        }}
                      />
                    ))}
                  {/*
                    Said here rather than left as a silent zero. A treatment
                    charged by the trunk cannot price until somebody puts a
                    tape round it, and the person who can do that is standing
                    in front of it right now.
                  */}
                  {needsDbh && dbhInches === null ? (
                    <p className="text-xs text-warning">
                      Measure the trunk diameter above to price this.
                    </p>
                  ) : null}
                  {solution.pricings.length === 0 ? (
                    <p className="text-xs text-muted-text">
                      Recorded, but not charged for.
                    </p>
                  ) : null}
                  <SeasonPicker
                    value={chosen.season}
                    current={seasons.current}
                    label={`Season for ${solution.label}`}
                    controlClassName={controlClassName}
                    onChange={(season) => {
                      setSeason(solution.id, season)
                    }}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
